import type { SubLayerType } from '../types';

/**
 * Default monochrome icons for a sub-layer's geometry type (line / point / polygon /
 * circle), shown in the info popover's "Included Sub-Layers" list. These are the type
 * defaults; the consumer derives the real per-layer icon + color from the layer data.
 *
 * All glyphs use `currentColor`, so the consumer sets the color via `className`.
 */

type SubLayerTypeIconProps = {
  type: SubLayerType;
  size?: number;
  className?: string;
};

function LineGlyph() {
  return (
    <path
      d="M1 11L5 6L9 9L13 3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

function PointGlyph() {
  return (
    <>
      <circle cx="3.5" cy="10.5" r="1.6" fill="currentColor" />
      <circle cx="7" cy="4.5" r="1.6" fill="currentColor" />
      <circle cx="10.5" cy="9.5" r="1.6" fill="currentColor" />
    </>
  );
}

function PolygonGlyph() {
  return (
    <path
      d="M7 1.5L12.5 5.5L10.4 12.2H3.6L1.5 5.5L7 1.5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinejoin="round"
    />
  );
}

function CircleGlyph() {
  return <circle cx="7" cy="7" r="5.2" fill="none" stroke="currentColor" strokeWidth="1.25" />;
}

/** Picture frame for raster/imaging sub-layers. */
function ImageGlyph() {
  return (
    <>
      <rect x="1.4" y="2.4" width="11.2" height="9.2" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.25" />
      <circle cx="4.6" cy="5.4" r="0.9" fill="currentColor" />
      <path d="M2.6 10.4L5.6 7.4L7.4 9.2L9.2 7L11.4 9.6V10.4H2.6Z" fill="currentColor" strokeLinejoin="round" />
    </>
  );
}

const GLYPHS: Record<SubLayerType, () => React.JSX.Element> = {
  line: LineGlyph,
  point: PointGlyph,
  polygon: PolygonGlyph,
  circle: CircleGlyph,
  image: ImageGlyph,
};

/** Renders the default icon for a sub-layer geometry type. */
function SubLayerTypeIcon({ type, size = 14, className }: SubLayerTypeIconProps) {
  const Glyph = GLYPHS[type];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={`${type} layer`}
    >
      <Glyph />
    </svg>
  );
}

export { SubLayerTypeIcon };
export type { SubLayerTypeIconProps };
